import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text } from '../ui'
import { supportCenterAttendService } from '../../services/supportCenterAttend'
import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import type { SupportCenterFormKind } from '../../types/supportCenterSpecialties'

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

type SimpleAttendanceNotesFormProps = {
  appointment: SupportCenterAgendaEvent
  formKind: Extract<
    SupportCenterFormKind,
    'nutricional' | 'fitness' | 'juridico' | 'social'
  >
  canSubmit: boolean
  forceReadOnly?: boolean
  label: string
  onClose: () => void
  onSubmitted: () => void
  onDraftSaved?: () => void
  /** Rodapé fixo do modal. Os botões de ação são renderizados nele. */
  actionsHost: HTMLElement | null
}

export const SimpleAttendanceNotesForm: React.FC<SimpleAttendanceNotesFormProps> = ({
  appointment,
  formKind,
  canSubmit,
  forceReadOnly = false,
  label,
  onClose,
  onSubmitted,
  onDraftSaved,
  actionsHost,
}) => {
  const [notes, setNotes] = React.useState('')
  const [submittedAt, setSubmittedAt] = React.useState<string | null>(null)
  const [loadingNotes, setLoadingNotes] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [confirmKind, setConfirmKind] = React.useState<'draft' | 'send' | null>(null)

  /** Somente leitura após envio do formulário (submitted_at), não após finalizar o atendimento. */
  const readOnly = forceReadOnly || !!submittedAt
  const fieldsDisabled = readOnly || !canSubmit || saving || loadingNotes

  React.useEffect(() => {
    let cancelled = false
    setSaving(false)
    setError(null)
    setConfirmKind(null)
    setLoadingNotes(true)
    void supportCenterAttendService
      .getAttendanceNotes(formKind, appointment.id)
      .then((row) => {
        if (cancelled) return
        setNotes(row?.notes ?? '')
        setSubmittedAt(row?.submitted_at ?? null)
      })
      .catch((e) => {
        if (cancelled) return
        setNotes('')
        setSubmittedAt(null)
        setError(e instanceof Error ? e.message : 'Não foi possível carregar o formulário.')
      })
      .finally(() => {
        if (!cancelled) setLoadingNotes(false)
      })
    return () => {
      cancelled = true
    }
  }, [appointment.id, formKind])

  const requestDraftConfirm = () => {
    if (!canSubmit || saving || readOnly) return
    setError(null)
    setConfirmKind('draft')
  }

  const handleSaveDraft = async () => {
    if (!canSubmit || saving || readOnly) return
    setSaving(true)
    setError(null)
    try {
      await supportCenterAttendService.saveAttendanceNotesDraft(formKind, appointment.id, notes)
      setConfirmKind(null)
      onDraftSaved?.()
      onClose()
    } catch (e) {
      setConfirmKind(null)
      setError(e instanceof Error ? e.message : 'Não foi possível salvar o rascunho.')
    } finally {
      setSaving(false)
    }
  }

  const requestSendConfirm = () => {
    if (!canSubmit || saving || readOnly) return
    if (!notes.trim()) {
      setError(`Informe ${label.charAt(0).toLowerCase()}${label.slice(1)}.`)
      return
    }
    setError(null)
    setConfirmKind('send')
  }

  const handleSend = async () => {
    if (!canSubmit || saving || readOnly) return
    setSaving(true)
    setError(null)
    try {
      await supportCenterAttendService.submitAttendanceNotes(formKind, appointment.id, notes)
      setSubmittedAt(new Date().toISOString())
      setConfirmKind(null)
      onSubmitted()
    } catch (e) {
      setConfirmKind(null)
      setError(e instanceof Error ? e.message : 'Não foi possível concluir o formulário.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <Text size="sm" color="muted">
        {readOnly
          ? 'Formulário concluído: somente leitura.'
          : 'Salve o rascunho quando quiser; ao concluir, não será possível alterar.'}
      </Text>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label} {!readOnly && <span className="text-red-500">*</span>}
        </label>
        <textarea
          rows={6}
          value={loadingNotes ? 'Carregando...' : notes}
          disabled={fieldsDisabled}
          onChange={(e) => setNotes(e.target.value)}
          className={`${inputClass} resize-none`}
        />
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {actionsHost
        ? createPortal(
            <>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                {readOnly ? 'Fechar' : 'Cancelar'}
              </Button>
              {canSubmit && !readOnly && (
                <>
                  <Button
                    variant="outline"
                    onClick={requestDraftConfirm}
                    disabled={saving || loadingNotes}
                  >
                    Salvar rascunho
                  </Button>
                  <Button onClick={requestSendConfirm} disabled={saving || loadingNotes}>
                    Concluir
                  </Button>
                </>
              )}
            </>,
            actionsHost
          )
        : null}

      {confirmKind &&
        createPortal(
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                {confirmKind === 'draft' ? 'Salvar rascunho' : 'Concluir formulário'}
              </Heading>
              <Text size="sm" color="muted">
                {confirmKind === 'draft'
                  ? 'Ao confirmar, o rascunho será salvo e o formulário será fechado. Deseja continuar?'
                  : 'Após concluir, não será possível alterar o formulário. Deseja continuar?'}
              </Text>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setConfirmKind(null)} disabled={saving}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => void (confirmKind === 'draft' ? handleSaveDraft() : handleSend())}
                  disabled={saving}
                >
                  {saving
                    ? confirmKind === 'draft'
                      ? 'Salvando...'
                      : 'Concluindo...'
                    : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
